import { parse } from "@babel/parser";
import { BuildNode } from "@common/build/types";

import type { JSXElement, JSXText } from "@babel/types";
import { deepCopy, getAllDescendantsOfNode } from "@builder/store/slices/BuilderSlice/builderHelpers";
import { getComponent, getUIComponent } from "@builder/store/slices/BuilderSlice/builderMap";
import { BuildComponentKey } from "@common/build/types";
import { DEFAULT_PROP_MAPPINGS, JSX_NODE_MAPPINGS, PropMapping } from "./jsxMapping";

export function _upperCaseFirstLetter(str: string): string {
    return str.charAt(0).toUpperCase() + str.slice(1);
}

export function _lowerCaseFirstLetter(str: string): string {
    return str.charAt(0).toLowerCase() + str.slice(1);
}

export function getValueAtPath(obj: any, path: string | (string | number)[]): any {
    const parts: (string | number)[] = Array.isArray(path) ? path : path.split(".");
    return parts.reduce((current, key) => (current as any)?.[key], obj as any);
}

export const setValueAtPath = (obj: any, path: (string | number)[], value: any): void => {
    let current: any = obj;
    for (let i = 0; i < path.length; i++) {
        const property = path[i];
        if (i === path.length - 1) {
            if (typeof value === "object" && !Array.isArray(value) && typeof current === "object") {
                current[property as string] = {
                    ...current[property as string],
                    ...value
                };
            } else {
                current[property] = value;
            }
        } else {
            // Create intermediate objects if they don't exist
            if (typeof current[property] === "object") {
                current = current[property];
            } else {
                let newValue: any = Array.isArray(current) ? [] : {};
                current[property] = newValue;
                current = newValue;
            }
        }
    }
};

// NEW: Remove a value (and key) from an object at a given path
export const deleteValueAtPath = (obj: any, path: (string | number)[]): void => {
    let current: any = obj;
    for (let i = 0; i < path.length; i++) {
        const property = path[i];
        if (i === path.length - 1) {
            if (Array.isArray(current)) {
                delete current[property as number];
            } else if (current && typeof current === "object") {
                delete current[property as string];
            }
        } else {
            if (!current || typeof current !== "object") return; // Nothing to delete
            current = current[property];
        }
    }
};

export function getExistingComponentAndItsChildren(jsxElement: JSXElement, fromNodes: BuildNode[] | undefined, parentId: string | null): BuildNode[] {
    if (!fromNodes) return [];
    const elementId = getElementId(jsxElement);
    if (!elementId) return [];

    const existingIndex = fromNodes.findIndex(n => n.id === elementId);
    if (existingIndex === -1) return [];

    const existingNode = fromNodes.splice(existingIndex, 1)[0];
    const descendants = getAllDescendantsOfNode(existingNode, fromNodes);
    descendants.forEach(d => {
        const idx = fromNodes.findIndex(n => n.id === d.id);
        if (idx !== -1) {
            fromNodes.splice(idx, 1);
        }
    });
    const copies = [existingNode, ...descendants].map(deepCopy);
    copies[0].parent = parentId || null;
    return copies;
}

export function getJSXAttributes(jsxElement: JSXElement): Record<string, any> {
    const attrs: Record<string, any> = {};
    jsxElement.openingElement?.attributes?.forEach(attr => {
        if (attr.type === "JSXAttribute" && (attr.name as any)?.name) {
            const attrKey = (attr.name as any).name as string;
            attrs[attrKey] = getJSXAttributeValue(attr.value);
        }
    });
    return attrs;
}

export function buildJSXFromNode(node: BuildNode, childrenJSX: string): string {
    const props: Record<string, any> = { id: node.id, ...nodeToJSXProps(node) };
    const extraInstructions = JSX_NODE_MAPPINGS[node.name as BuildComponentKey]?.extraInstructions;

    const propsString = Object.entries(props)
        .filter(([, value]) => value !== undefined)
        .map(([propName, propValue]) => {
            if (typeof propValue === "string") {
                const cleanStr = propValue.trim();
                if (cleanStr.includes("\n")) {
                    return `${propName}={\`${cleanStr}\`}`;
                }
                return `${propName}=${JSON.stringify(cleanStr)}`;
            }
            return `${propName}={${JSON.stringify(propValue)}}`;
        })
        .join(" ");

    const componentName = _upperCaseFirstLetter(node.name);
    const extraComment = extraInstructions ? `{/* ${extraInstructions} */}\n` : "";

    if (childrenJSX && childrenJSX.length > 0) {
        return `${extraComment}<${componentName}${propsString ? " " + propsString : ""}>${childrenJSX}</${componentName}>`;
    }
    return `${extraComment}<${componentName}${propsString ? " " + propsString : ""}/>`;
}

export function nodeToJSXProps(node: BuildNode): Record<string, any> {
    const jsxProps: Record<string, any> = {};
    const propsToIgnore = ["children", "parent", "id", "nodeSize"];

    // Keep track of which top-level keys were already handled by the default mappings so that
    // we do not add them again when iterating over the rest of the node properties.
    const mappedTopLevelKeys = new Set<string>();

    DEFAULT_PROP_MAPPINGS.forEach(mapping => {
        const nodeValue = getValueAtPath(node, mapping.nodePath);
        if (nodeValue !== undefined) {
            jsxProps[mapping.jsxProp] = nodeValue;
        }

        // Make sure we skip the original key that this mapping points to when we iterate later.
        const pathArr = Array.isArray(mapping.nodePath) ? (mapping.nodePath as (string | number)[]) : (mapping.nodePath as string).split(".");
        if (pathArr.length) {
            mappedTopLevelKeys.add(String(pathArr[0]));
        }
    });

    Object.entries(node).forEach(([key, value]) => {
        if (propsToIgnore.includes(key)) return;
        if (mappedTopLevelKeys.has(key)) return; // already handled above
        if (jsxProps[key] === undefined) {
            jsxProps[key] = value;
        }
    });

    return jsxProps;
}

function isEmptyObject(obj: any): boolean {
    return Object.keys(obj).length === 0;
}


export function applyJSXPropToNode(propName: string, propValue: any, node: BuildNode, additionalPropMappings: PropMapping[] = []): void {
    const mapping = [...DEFAULT_PROP_MAPPINGS, ...additionalPropMappings].find(m => m.jsxProp === propName);

    if (!mapping) return;

    // If propValue is undefined, we want to remove the existing value at the mapped path
    if (propValue === undefined || propValue === null || isEmptyObject(propValue)) {
        deleteValueAtPath(node, Array.isArray(mapping.nodePath) ? mapping.nodePath : (mapping.nodePath as string).split("."));
        return;
    }

    if (mapping.customAddJSXAttToNode) {
        mapping.customAddJSXAttToNode(node, propValue);
    } else {
        // Special handling for textable attributes – only set if value is a string
        if ((mapping.jsxProp === "text" || mapping.jsxProp === "children") && typeof propValue !== "string") {
            return;
        }
        setValueAtPath(
            node,
            Array.isArray(mapping.nodePath) ? mapping.nodePath : (mapping.nodePath as string).split("."),
            propValue
        );
    }
}

export function isJSX(jsxString: string): boolean {
    try {
        parse(jsxString, {
            sourceType: "module",
            plugins: ["jsx", "typescript"],
        });
        return true;
    } catch (e) {
        console.log("not a valid jsx string", e);
        return false;
    }
}


//@ts-ignore
window.parseJSX = parse;

// technical helper functions:
export function getElementName(jsxElement: JSXElement): string | null {
    const nameNode = jsxElement.openingElement?.name;
    if (nameNode?.type === "JSXIdentifier") {
        return nameNode.name;
    }
    return null;
}

export function getElementId(jsxElement: JSXElement): string | null {
    const idNode = jsxElement.openingElement?.attributes?.find(attr => attr.type === "JSXAttribute" && attr.name.name === "id");
    if (idNode?.type === "JSXAttribute" && idNode.name.name === "id") {
        return getJSXAttributeValue(idNode.value);
    }
    return null;
}

export function getJSXAttributeValue(valueNode: any): any {
    if (!valueNode) return true;
    switch (valueNode.type) {
        case "StringLiteral":
            return valueNode.value;
        case "JSXExpressionContainer":
            return evaluateJSXExpression(valueNode.expression);
        case "Literal":
            return valueNode.value;
        default:
            return null;
    }
}

export function evaluateJSXExpression(expression: any): any {
    switch (expression.type) {
        case "StringLiteral":
        case "Literal":
            return expression.value;
        case "NumericLiteral":
            return expression.value;
        case "BooleanLiteral":
            return expression.value;
        case "ObjectExpression": {
            const obj: Record<string, any> = {};
            expression.properties?.forEach((prop: any) => {
                if (prop.type === "ObjectProperty") {
                    const key = (prop.key as any).name || (prop.key as any).value;
                    const value = evaluateJSXExpression(prop.value);
                    obj[key] = value;
                }
            });
            return obj;
        }
        case "ArrayExpression":
            return expression.elements?.map((elem: any) => evaluateJSXExpression(elem)) || [];
        default:
            return null;
    }
}

export function getNewOrExistingComponent({
    name,
    jsxElement,
    parentId,
    fromNodes,
    withChildren = false
}: {
    name?: BuildComponentKey;
    jsxElement: JSXElement;
    parentId?: string | null;
    fromNodes?: BuildNode[];
    withChildren?: boolean;
}): BuildNode | BuildNode[] {
    const elementId = getElementId(jsxElement);
    if (elementId && fromNodes) {
        const existingIndex = fromNodes.findIndex(n => n.id === elementId);
        if (existingIndex !== -1) {
            const existingNode = fromNodes.splice(existingIndex, 1)[0];
            if (withChildren) {
                const descendants = getAllDescendantsOfNode(existingNode, fromNodes).map(deepCopy);
                const parentCopy = deepCopy(existingNode);
                parentCopy.parent = parentId || null;
                return [parentCopy, ...descendants];
            }
            const newComponent = deepCopy(existingNode);
            newComponent.children = [];
            newComponent.parent = parentId || null;
            return newComponent;
        }
    }
    return getComponent(name || "div", parentId || undefined);
}

export function validateChildInParent(childNode: BuildNode, parentNode: BuildNode, nodeMap: Record<string, BuildNode>) {
    const parentMapping: any = JSX_NODE_MAPPINGS[parentNode.name as BuildComponentKey];
    if (parentMapping?.allowedChildNames && !parentMapping.allowedChildNames.includes(childNode.name as BuildComponentKey)) {
        throw new Error(`Invalid child <${childNode.name}> inside <${parentNode.name}>. Allowed: ${parentMapping.allowedChildNames.join(", ")}`);
    }

    const childUI = getUIComponent(childNode.name as BuildComponentKey);
    if (childUI?.restrictToParents && childUI.restrictToParents.length > 0) {
        let ancestor: BuildNode | undefined = parentNode;
        let permitted = false;
        while (ancestor) {
            if (childUI.restrictToParents.includes(ancestor.name as BuildComponentKey)) {
                permitted = true;
                break;
            }
            if (ancestor.parent) {
                ancestor = nodeMap[ancestor.parent];
            } else {
                break;
            }
        }
        if (!permitted) {
            throw new Error(`<${childNode.name}> must be nested within ${childUI.restrictToParents.join(", ")}`);
        }
    }
}

export function convertJSXTextToTextComponent(textNode: JSXText): JSXElement | null {
    const raw = textNode.value ?? "";
    const value = raw.trim();
    if (value.length === 0) return null;
    const element: JSXElement = {
        type: "JSXElement",
        openingElement: {
            type: "JSXOpeningElement",
            name: { type: "JSXIdentifier", name: "Text" },
            attributes: [
                {
                    type: "JSXAttribute",
                    name: { type: "JSXIdentifier", name: "text" },
                    value: { type: "StringLiteral", value }
                } as any
            ],
            selfClosing: true,
            typeParameters: null
        } as any,
        closingElement: null,
        children: [],
        trailingComments: null as any,
        leadingComments: null as any,
        innerComments: null as any
    } as any;
    return element;
}

export function jsxAttrsToNode({
    attributes,
    node,
    allowedProps,
    additionalPropMappings = []
}: {
    attributes: any[];
    node: BuildNode;
    allowedProps?: string[];
    additionalPropMappings?: PropMapping[];
}): void {
    const mappings = [...DEFAULT_PROP_MAPPINGS, ...additionalPropMappings];

    // Build a quick lookup for incoming attribute values
    const attrValues: Record<string, any> = {};
    attributes.forEach(a => {
        if (a.type === "JSXAttribute" && (a.name as any)?.name) {
            const key = (a.name as any).name as string;
            attrValues[key] = getJSXAttributeValue(a.value);
        }
    });

    mappings.forEach(mapping => {
        const propName = mapping.jsxProp;
        if (allowedProps && !allowedProps.includes(propName)) return;
        const incomingValue = attrValues.hasOwnProperty(propName) ? attrValues[propName] : undefined;
        applyJSXPropToNode(propName, incomingValue, node, additionalPropMappings);
    });
}

export function JSXElementToBuilderNode(
    jsxElement: JSXElement,
    parentId: string | null,
    fromNodes?: BuildNode[],
    additionalPropMappings: PropMapping[] = []
): BuildNode[] {
    const elementName = getElementName(jsxElement);
    const nodeKey = elementName ? (_lowerCaseFirstLetter(elementName) as BuildComponentKey) : "div";
    const mappingForNode = JSX_NODE_MAPPINGS[nodeKey as BuildComponentKey];

    // If component is textable, extract direct JSXText children as text content
    let extractedText: string | null = null;
    if (mappingForNode?.isTextable && Array.isArray((jsxElement as any).children)) {
        const textParts: string[] = [];
        const newChildren: any[] = [];
        for (const ch of (jsxElement as any).children) {
            if (ch && ch.type === "JSXText") {
                const raw = (ch.value ?? "") as string;
                const trimmed = raw.trim();
                if (trimmed.length > 0) textParts.push(trimmed);
                // skip adding this JSXText to children to prevent fallback conversion
            } else {
                newChildren.push(ch);
            }
        }
        (jsxElement as any).children = newChildren;
        if (textParts.length > 0) {
            extractedText = textParts.join(" ");
        }
    }

    const componentOrComponents = getNewOrExistingComponent({
        name: nodeKey as BuildComponentKey,
        jsxElement,
        parentId,
        fromNodes,
        withChildren: false
    });

    if (Array.isArray(componentOrComponents)) {
        if (extractedText) {
            componentOrComponents[0].text = extractedText;
        }
        return componentOrComponents as BuildNode[];
    }

    const node = componentOrComponents as BuildNode;
    if (extractedText) {
        node.text = extractedText;
    }

    if (jsxElement.openingElement?.attributes) {
        jsxAttrsToNode({ attributes: jsxElement.openingElement.attributes, node, additionalPropMappings });
    }

    const preset = (node as any).preset;
    if (preset && preset.length > 0) {
        const presetNodes = deepCopy(preset);
        const rootNode = presetNodes[0];
        rootNode.parent = parentId || null;
        if (jsxElement.openingElement?.attributes) {
            jsxAttrsToNode({ attributes: jsxElement.openingElement.attributes, node: rootNode, additionalPropMappings });
        }
        return presetNodes;
    }

    return [node];
}

export function unimplementedNodeJsxToBuilder(jsxElement: JSXElement, parentId: string | null, fromNodes?: BuildNode[]): BuildNode[] {
    console.log("unimplementedNodeJsxToBuilder", getElementName(jsxElement));
    if (fromNodes && fromNodes.length) {
        const existingSubtree = getExistingComponentAndItsChildren(jsxElement, fromNodes, parentId);
        if (existingSubtree.length) {
            const parentNode = existingSubtree[0];
            if (jsxElement.openingElement?.attributes) {
                jsxAttrsToNode({ attributes: jsxElement.openingElement.attributes, node: parentNode, allowedProps: ["style"] });
            }
            return existingSubtree;
        }
    }

    const node = getComponent("div", parentId || undefined);
    if (jsxElement.openingElement?.attributes) {
        jsxAttrsToNode({ attributes: jsxElement.openingElement.attributes, node, allowedProps: ["style", "nodeSize"] });
    }
    return [node];
}
